import CallRoundedIcon from '@mui/icons-material/CallRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import NoteAltRoundedIcon from '@mui/icons-material/NoteAltRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import { createTheme } from '@mui/material/styles';
import {
  formatDate,
  formatDateTime,
  formatEnumLabel,
  formatValue,
  safeDate,
} from '../../utils/crmFormatting';
import { pageColors } from '../HomePage';

export { formatDate, formatDateTime, formatEnumLabel, formatValue, safeDate };

export const CRM_THEME = createTheme({
  palette: {
    primary: {
      main: pageColors.customers,
    },
  },
  typography: {
    fontFamily: 'Inter, Roboto, sans-serif',
  },
});

export const PAGE_BACKGROUND =
  'radial-gradient(circle at top left, rgba(0,137,123,0.16), transparent 55%), #f5f7fb';

export const SURFACE_SX = {
  borderRadius: 4,
  backgroundColor: '#fff',
  boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
  border: '1px solid rgba(0,0,0,0.05)',
};

export const DETAIL_LAYOUT_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.08fr) minmax(360px, 0.92fr)' },
  gap: 2,
  alignItems: 'start',
};

export const READ_ONLY_FIELD_SX = {
  '& .MuiInputLabel-root': {
    color: 'text.secondary',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#f4f7f7',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0,0,0,0.08)',
      borderStyle: 'dashed',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0,0,0,0.08)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0,0,0,0.08)',
      borderWidth: 1,
    },
  },
  '& .MuiInputBase-input.Mui-readOnly': {
    color: 'text.primary',
    cursor: 'default',
    WebkitTextFillColor: 'inherit',
  },
};

export const TIMELINE_RAIL_SX = {
  position: 'relative',
  pl: 0.5,
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 17,
    top: 0,
    bottom: 0,
    width: 2,
    backgroundColor: 'rgba(0,137,123,0.12)',
  },
};

export const BADGE_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 24,
  px: 1,
  borderRadius: '999px',
  border: '1px solid transparent',
  fontSize: '0.75rem',
  fontWeight: 600,
  lineHeight: 1.1,
};

export const EMPTY_PROFILE_FORM = {
  priority: '',
  segment: '',
  buyingRhythmNotes: '',
  productPreferenceNotes: '',
  seasonalityNotes: '',
};

export const EMPTY_CONTACT_FORM = {
  name: '',
  title: '',
  phone: '',
  email: '',
  isPrimary: false,
  notes: '',
};

export const EMPTY_ACTIVITY_FORM = {
  activityType: 'CALL',
  title: '',
  note: '',
  state: 'OPEN',
  dueDate: '',
  assignedUserId: '',
  contactId: '',
  occurredAt: '',
};

export const ACTIVITY_TYPE_OPTIONS = [
  {
    value: 'INTERNAL_NOTE',
    label: 'Internal note',
    Icon: NoteAltRoundedIcon,
    color: '#00695c',
    background: 'rgba(0,137,123,0.10)',
    border: 'rgba(0,137,123,0.18)',
  },
  {
    value: 'CALL',
    label: 'Call',
    Icon: CallRoundedIcon,
    color: '#1565c0',
    background: 'rgba(21,101,192,0.10)',
    border: 'rgba(21,101,192,0.18)',
  },
  {
    value: 'VISIT',
    label: 'Visit',
    Icon: StorefrontRoundedIcon,
    color: '#ef6c00',
    background: 'rgba(239,108,0,0.10)',
    border: 'rgba(239,108,0,0.18)',
  },
  {
    value: 'EMAIL',
    label: 'Email',
    Icon: EmailRoundedIcon,
    color: '#6a1b9a',
    background: 'rgba(106,27,154,0.10)',
    border: 'rgba(106,27,154,0.18)',
  },
  {
    value: 'ISSUE',
    label: 'Issue',
    Icon: ReportProblemRoundedIcon,
    color: '#c62828',
    background: 'rgba(198,40,40,0.10)',
    border: 'rgba(198,40,40,0.18)',
  },
  {
    value: 'OTHER',
    label: 'Other',
    Icon: NoteAltRoundedIcon,
    color: '#546e7a',
    background: 'rgba(84,110,122,0.10)',
    border: 'rgba(84,110,122,0.18)',
  },
];

export const ACTIVITY_TYPE_MAP = new Map(ACTIVITY_TYPE_OPTIONS.map((option) => [option.value, option]));

export const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

export const SEGMENT_OPTIONS = [
  { value: 'RESTAURANT', label: 'Restaurant' },
  { value: 'CAFE', label: 'Cafe' },
  { value: 'BAKERY', label: 'Bakery' },
  { value: 'DELI', label: 'Deli' },
];

export const ACTIVITY_STATUS_LABELS = {
  OPEN: 'Open',
  DONE: 'Done',
  CANCELLED: 'Cancelled',
};

export const ACTIVITY_CREATE_STATE_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'DONE', label: 'Done' },
];

export function normalizeOptionalString(value) {
  const trimmed = String(value ?? '').trim();
  return trimmed || null;
}

export function formatDateSeparator(value) {
  const resolved = safeDate(value);

  if (!resolved) {
    return 'Unknown date';
  }

  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(resolved);
}

export function buildDateSeparatorKey(value) {
  const normalized = String(value ?? '').trim();
  return normalized ? normalized.slice(0, 10) : 'unknown-date';
}

export function toDateTimeInputValue(value) {
  const normalized = String(value ?? '').trim();

  if (!normalized) {
    return '';
  }

  const withoutSeconds = normalized.replace(/\.\d+$/, '');
  return withoutSeconds.slice(0, 16);
}

export function toDateInputValue(value) {
  return String(value ?? '').trim().slice(0, 10);
}

export function getDoneActivitySortKey(activity) {
  return String(activity?.occurredAt ?? activity?.completedAt ?? activity?.createdAt ?? '').trim();
}

export function sortContacts(contacts) {
  return [...contacts].sort((left, right) => {
    const activeWeight = Number(Boolean(right?.active)) - Number(Boolean(left?.active));
    if (activeWeight !== 0) {
      return activeWeight;
    }

    const primaryWeight = Number(Boolean(right?.isPrimary)) - Number(Boolean(left?.isPrimary));
    if (primaryWeight !== 0) {
      return primaryWeight;
    }

    const leftName = String(left?.name ?? '').trim().toLowerCase();
    const rightName = String(right?.name ?? '').trim().toLowerCase();
    const nameCompare = leftName.localeCompare(rightName);
    if (nameCompare !== 0) {
      return nameCompare;
    }

    return Number(left?.id ?? 0) - Number(right?.id ?? 0);
  });
}

export function sortDoneActivities(entries) {
  return [...entries].sort((left, right) => {
    const rightKey = getDoneActivitySortKey(right);
    const leftKey = getDoneActivitySortKey(left);
    const keyCompare = rightKey.localeCompare(leftKey);
    if (keyCompare !== 0) {
      return keyCompare;
    }

    return Number(right?.id ?? 0) - Number(left?.id ?? 0);
  });
}

function mergeByIdAndSort(currentItems, nextItem, sortItems) {
  const nextId = Number(nextItem?.id ?? 0);
  const mergedItems = currentItems.some((item) => Number(item?.id ?? 0) === nextId)
    ? currentItems.map((item) => (Number(item?.id ?? 0) === nextId ? nextItem : item))
    : [...currentItems, nextItem];

  return sortItems(mergedItems);
}

export function mergeDoneActivityIntoList(currentEntries, nextEntry) {
  return mergeByIdAndSort(currentEntries, nextEntry, sortDoneActivities);
}

export function sortActivities(activities) {
  return [...activities].sort((left, right) => {
    const dueCompare = String(left?.dueDate ?? '').localeCompare(String(right?.dueDate ?? ''));
    if (dueCompare !== 0) {
      return dueCompare;
    }

    return Number(left?.id ?? 0) - Number(right?.id ?? 0);
  });
}

export function mergeContactIntoList(currentContacts, nextContact) {
  const nextId = Number(nextContact?.id ?? 0);
  const mergedContacts = currentContacts.some((contact) => Number(contact?.id ?? 0) === nextId)
    ? currentContacts.map((contact) => (Number(contact?.id ?? 0) === nextId ? nextContact : contact))
    : [...currentContacts, nextContact];

  const normalizedContacts =
    nextContact?.active && nextContact?.isPrimary
      ? mergedContacts.map((contact) =>
          Number(contact?.id ?? 0) === nextId || !contact?.active
            ? contact
            : { ...contact, isPrimary: false }
        )
      : mergedContacts;

  return sortContacts(normalizedContacts);
}

export function mergeActivityIntoList(currentActivities, nextActivity) {
  return mergeByIdAndSort(currentActivities, nextActivity, sortActivities);
}

export function mapProfileToForm(profile) {
  return {
    priority: String(profile?.priority ?? '').trim(),
    segment: String(profile?.segment ?? '').trim(),
    buyingRhythmNotes: String(profile?.buyingRhythmNotes ?? ''),
    productPreferenceNotes: String(profile?.productPreferenceNotes ?? ''),
    seasonalityNotes: String(profile?.seasonalityNotes ?? ''),
  };
}

export function mapContactToForm(contact) {
  return {
    name: String(contact?.name ?? ''),
    title: String(contact?.title ?? ''),
    phone: String(contact?.phone ?? ''),
    email: String(contact?.email ?? ''),
    isPrimary: Boolean(contact?.isPrimary),
    notes: String(contact?.notes ?? ''),
  };
}

export function buildProfilePayload(formValues) {
  return {
    priority: formValues.priority || null,
    segment: formValues.segment || null,
    buyingRhythmNotes: normalizeOptionalString(formValues.buyingRhythmNotes),
    productPreferenceNotes: normalizeOptionalString(formValues.productPreferenceNotes),
    seasonalityNotes: normalizeOptionalString(formValues.seasonalityNotes),
  };
}

export function buildContactPayload(formValues, { active } = {}) {
  const payload = {
    name: String(formValues.name ?? '').trim(),
    title: normalizeOptionalString(formValues.title),
    phone: normalizeOptionalString(formValues.phone),
    email: normalizeOptionalString(formValues.email),
    isPrimary: Boolean(formValues.isPrimary),
    notes: normalizeOptionalString(formValues.notes),
  };

  if (typeof active === 'boolean') {
    payload.active = active;
  }

  return payload;
}

export function buildActivityPayload(formValues, stateOverride) {
  const activityType = String(formValues.activityType ?? 'CALL').trim() || 'CALL';
  const state = String(stateOverride ?? formValues.state ?? 'OPEN').trim() || 'OPEN';

  return {
    activityType,
    title: String(formValues.title ?? '').trim(),
    note: normalizeOptionalString(formValues.note),
    state,
    dueDate: state === 'OPEN' ? toDateInputValue(formValues.dueDate) : null,
    assignedUserId: formValues.assignedUserId ? Number(formValues.assignedUserId) : null,
    contactId: formValues.contactId ? Number(formValues.contactId) : null,
    occurredAt: state === 'DONE' ? normalizeOptionalString(formValues.occurredAt) : null,
  };
}

export function buildActivityActionPayload(activity, state) {
  return {
    activityType: String(activity?.activityType ?? 'CALL').trim() || 'CALL',
    title: String(activity?.title ?? '').trim(),
    note: normalizeOptionalString(activity?.note),
    state,
    dueDate: toDateInputValue(activity?.dueDate),
    assignedUserId: activity?.assignedUser?.id ? Number(activity.assignedUser.id) : null,
    contactId: activity?.contact?.id ? Number(activity.contact.id) : null,
    occurredAt: activity?.occurredAt ?? null,
  };
}

export function mapActivityToForm(activity) {
  return {
    activityType: String(activity?.activityType ?? 'CALL'),
    title: String(activity?.title ?? ''),
    note: String(activity?.note ?? ''),
    state: String(activity?.state ?? 'OPEN'),
    dueDate: toDateInputValue(activity?.dueDate),
    assignedUserId: activity?.assignedUser?.id ? String(activity.assignedUser.id) : '',
    contactId: activity?.contact?.id ? String(activity.contact.id) : '',
    occurredAt: toDateTimeInputValue(activity?.occurredAt),
  };
}

export function isActivityOpen(activity) {
  return String(activity?.state ?? '') === 'OPEN';
}

export function formatUserSummary(user) {
  return String(user?.displayName ?? '').trim() || 'Unknown user';
}

export function formatAssignableUserLabel(user) {
  return String(user?.displayName ?? user?.username ?? '').trim();
}

export function getActivityAuditLine(activity) {
  const state = String(activity?.state ?? '');

  if (state === 'DONE') {
    return `Completed by ${formatUserSummary(activity?.completedBy)} on ${formatDateTime(activity?.completedAt)}`;
  }

  if (state === 'CANCELLED') {
    return `Cancelled by ${formatUserSummary(activity?.cancelledBy)} on ${formatDateTime(activity?.cancelledAt)}`;
  }

  return `Updated by ${formatUserSummary(activity?.updatedBy)} on ${formatDateTime(activity?.updatedAt)}`;
}

export function buildTimelineFeedItems(entries) {
  const items = [];
  let previousDateKey = null;

  entries.forEach((entry) => {
    const sortKey = getDoneActivitySortKey(entry);
    const dateKey = buildDateSeparatorKey(sortKey);

    if (dateKey !== previousDateKey) {
      items.push({
        type: 'separator',
        key: `separator-${dateKey}`,
        label: formatDateSeparator(sortKey),
      });
      previousDateKey = dateKey;
    }

    items.push({
      type: 'entry',
      key: `entry-${entry.id}`,
      entry,
    });
  });

  return items;
}

export function buildOverviewSummary({ activities, contacts, profileFormValues }) {
  const activeContacts = contacts.filter((contact) => Boolean(contact?.active));
  const primaryContact =
    activeContacts.find((contact) => Boolean(contact?.isPrimary)) ?? activeContacts[0] ?? null;
  const priorityLabel = formatEnumLabel(profileFormValues.priority, 'Not set');
  const segmentLabel = formatEnumLabel(profileFormValues.segment, 'Not set');

  return {
    openActivityCount: activities.length,
    nextActivity: activities[0] ?? null,
    primaryContact,
    priorityLabel,
    segmentLabel,
  };
}
